import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import DeviceInfo from '../../utils/deviceInfo.web';

import { apiClient, STORAGE_KEYS, handleApiError, AsyncStorage } from '../../config/api.web';
import { LoginRequest, LoginResponse, UserInfo, RefreshTokenRequest } from '../../types/api';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserInfo | null;
  accessToken: string | null;
  refreshToken: string | null;
  error: string | null;
  deviceId: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  refreshToken: null,
  error: null,
  deviceId: null,
};

// Async Thunks
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const [accessToken, refreshToken, userInfo, deviceId] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
        AsyncStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
        AsyncStorage.getItem(STORAGE_KEYS.USER_INFO),
        AsyncStorage.getItem(STORAGE_KEYS.DEVICE_ID),
      ]);

      if (accessToken && refreshToken && userInfo) {
        return {
          accessToken,
          refreshToken,
          user: JSON.parse(userInfo),
          deviceId,
        };
      }

      return null;
    } catch (error) {
      return rejectWithValue('Failed to initialize auth state');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: Omit<LoginRequest, 'deviceId' | 'deviceType' | 'appVersion'>, { rejectWithValue }) => {
    try {
      // Get device information for web
      const deviceId = await DeviceInfo.getUniqueId();
      const deviceType = await DeviceInfo.getSystemName();
      const appVersion = await DeviceInfo.getVersion();

      const loginData: LoginRequest = {
        ...credentials,
        deviceId,
        deviceType: 'Web',
        appVersion,
      };

      const response = await apiClient.post<LoginResponse>('/auth/employee/login', loginData);

      if (!response.success || !response.accessToken || !response.refreshToken) {
        return rejectWithValue(response.message || 'Login failed');
      }

      // Store tokens and user info
      await Promise.all([
        AsyncStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken),
        AsyncStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken),
        AsyncStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(response.user)),
        AsyncStorage.setItem(STORAGE_KEYS.DEVICE_ID, deviceId),
      ]);

      return {
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        user: response.user,
        deviceId,
      };
    } catch (error: any) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const { refreshToken } = state.auth;

      if (refreshToken) {
        try {
          await apiClient.post('/auth/logout', { refreshToken });
        } catch (error) {
          // Continue with logout even if API call fails
          console.warn('Logout API call failed, continuing with local logout');
        }
      }

      // Clear stored data
      await AsyncStorage.multiRemove([
        STORAGE_KEYS.ACCESS_TOKEN,
        STORAGE_KEYS.REFRESH_TOKEN,
        STORAGE_KEYS.USER_INFO,
        STORAGE_KEYS.DEVICE_ID,
      ]);

      return null;
    } catch (error: any) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  'auth/refresh',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: AuthState };
      const { refreshToken } = state.auth;

      if (!refreshToken) {
        return rejectWithValue('No refresh token available');
      }

      const refreshData: RefreshTokenRequest = { refreshToken };
      const response = await apiClient.post<LoginResponse>('/auth/refresh', refreshData);

      if (!response.success || !response.accessToken) {
        return rejectWithValue(response.message || 'Token refresh failed');
      }

      // Update stored access token
      await AsyncStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);

      return {
        accessToken: response.accessToken,
        user: response.user,
      };
    } catch (error: any) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.accessToken = action.payload.accessToken;
          state.refreshToken = action.payload.refreshToken;
          state.user = action.payload.user;
          state.deviceId = action.payload.deviceId;
        } else {
          state.isAuthenticated = false;
        }
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.user = action.payload.user;
        state.deviceId = action.payload.deviceId;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.deviceId = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Refresh Token
      .addCase(refreshAccessToken.pending, (state) => {
        state.error = null;
      })
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        if (action.payload.user) {
          state.user = action.payload.user;
        }
        state.error = null;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setError } = authSlice.actions;
export default authSlice.reducer;